import { useStaffMode } from "../../../hooks/StaffModeProvider";
import { usePageTitle } from "../../../hooks/usePageTitle";
import {
  IPageMetaAction,
  useRoutePageMetaSnapshot,
} from "../../../hooks/pageMetaStore";
import { Link, useLocation } from "react-router-dom";
import PencilEdit from "../../Atomos/Icons/PencilEdit";
import { MemberTitleLegacyAction } from "./MemberTitleLegacyAction";

interface TitleLayoutProps {
  bannerPatternImage: string;
}

const memberTitleActionSolidClassName =
  "font-decimal inline-flex shrink-0 items-center justify-center gap-3 rounded border border-primary bg-primary py-[11px] pl-4 pr-5 text-sm font-semibold text-white no-underline transition-colors hover:border-primary-dark hover:bg-primary-dark md:text-base";

const memberTitleActionSolidSecondaryClassName =
  "font-decimal inline-flex shrink-0 items-center justify-center gap-3 rounded border border-secondary bg-secondary py-[11px] pl-4 pr-5 text-sm font-semibold text-white no-underline transition-colors hover:border-secondary-dark hover:bg-accent-Default-dark md:text-base";

const memberTitleActionOutlineClassName =
  "font-decimal inline-flex shrink-0 items-center justify-center gap-3 rounded-md border border-white bg-transparent py-[11px] pl-4 pr-5 text-sm font-semibold text-white no-underline transition-colors hover:bg-white/15 hover:border-white md:text-base";

function MemberTitleActionContent({
  label,
  icon,
}: {
  label: string;
  icon?: React.ReactNode;
}) {
  return (
    <>
      <span>{label}</span>
      <span className="flex h-5 w-5 shrink-0 items-center justify-center text-white">
        {icon ?? <PencilEdit className="h-full w-full" />}
      </span>
    </>
  );
}

function memberTitleActionIsVisible(action: IPageMetaAction | undefined): boolean {
  return Boolean(action?.label && (action.to || action.onClick));
}

function memberTitleActionClassFor(action: NonNullable<Exclude<IPageMetaAction, null>>) {
  if (action.variant === "outline") {
    return memberTitleActionOutlineClassName;
  }
  return action.tone === "secondary"
    ? memberTitleActionSolidSecondaryClassName
    : memberTitleActionSolidClassName;
}

function MemberTitleActionLinkOrButton({
  action,
}: {
  action: NonNullable<Exclude<IPageMetaAction, null>>;
}) {
  if (action.legacyButtonColor != null) {
    return <MemberTitleLegacyAction action={action} />;
  }

  const className = memberTitleActionClassFor(action);
  const content = (
    <MemberTitleActionContent label={action.label} icon={action.icon} />
  );
  if (action.to) {
    return (
      <Link
        to={action.to}
        className={className}
        target={action.openInNewTab ? "_blank" : undefined}
        rel={action.openInNewTab ? "noopener noreferrer" : undefined}
      >
        {content}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={() => action.onClick?.()}
      className={className}
    >
      {content}
    </button>
  );
}

const TitleLayout = ({ bannerPatternImage }: TitleLayoutProps) => {
  const { isStaffMode } = useStaffMode();
  const { title: pageTitle, subtitle: pageSubtitle } = usePageTitle();
  const { pathname } = useLocation();
  const dynamicPageMeta = useRoutePageMetaSnapshot(pathname);
  const titleAction = dynamicPageMeta?.titleAction;
  const titleActionSecondary = dynamicPageMeta?.titleActionSecondary;
  const showMemberTitleAction = memberTitleActionIsVisible(titleAction ?? undefined);
  const showMemberTitleActionSecondary = memberTitleActionIsVisible(
    titleActionSecondary ?? undefined
  );

  return (
    <>
      {pageTitle && (
        <>
          {isStaffMode ?
            <div className="flex flex-col md:flex-row overflow-hidden gap-0 md:gap-4">
              <div className="min-w-0 flex-[0_1_auto] items-center flex-wrap gap-x-4 gap-y-2 text-white flex">
                <h1 className="text-center md:text-left min-w-0 [overflow-wrap:anywhere] font-medium">
                  {pageTitle}
                  <span className="text-[24px] lg:text-[32px] font-light font-decimal">
                    {`${pageSubtitle ? ` - ${pageSubtitle}` : ''}`}
                  </span>
                </h1>
              </div>
              <div
                className="hidden md:flex flex-1 min-w-0 bg-contain bg-repeat-x bg-right-center pointer-events-none"
                style={{ backgroundImage: `url(${bannerPatternImage})` }}
              />
              {(showMemberTitleAction && titleAction) ||
              (showMemberTitleActionSecondary && titleActionSecondary) ? (
                <div className="flex shrink-0 flex-wrap items-end justify-end gap-3 md:gap-4">
                  {showMemberTitleAction && titleAction ? (
                    <MemberTitleActionLinkOrButton action={titleAction} />
                  ) : null}
                  {showMemberTitleActionSecondary && titleActionSecondary ? (
                    <MemberTitleActionLinkOrButton action={titleActionSecondary} />
                  ) : null}
                </div>
              ) : null}
            </div>
            :
            <>
              <div className="hidden sm:flex items-center overflow-hidden mb-4 -ml-1 gap-4">
                <h1 className="mb-3 text-white font-medium text-left text-[40px] sm:text-[48px] lg:text-[50px] xl:text-[56px] 2xl:text-[62px] flex-shrink-0 pr-2">
                  {pageTitle}
                </h1>
                <div
                  className="flex-1 min-w-[48px] h-[60px] bg-contain bg-repeat-x bg-right-center pointer-events-none"
                  style={{ backgroundImage: `url(${bannerPatternImage})` }}
                />
                {showMemberTitleAction || showMemberTitleActionSecondary ? (
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 md:gap-4">
                    {showMemberTitleAction && titleAction ? (
                      <MemberTitleActionLinkOrButton action={titleAction} />
                    ) : null}
                    {showMemberTitleActionSecondary && titleActionSecondary ? (
                      <MemberTitleActionLinkOrButton action={titleActionSecondary} />
                    ) : null}
                  </div>
                ) : null}
              </div>

              {pageTitle && (
                <>
                  <h1 className="sm:hidden text-white font-medium text-left -ml-1 text-[40px] leading-[1.3] pb-2">
                    {pageTitle}
                  </h1>
                  {showMemberTitleAction || showMemberTitleActionSecondary ? (
                    <div className="mb-4 flex flex-wrap gap-3 sm:hidden">
                      {showMemberTitleAction && titleAction ? (
                        <MemberTitleActionLinkOrButton action={titleAction} />
                      ) : null}
                      {showMemberTitleActionSecondary && titleActionSecondary ? (
                        <MemberTitleActionLinkOrButton action={titleActionSecondary} />
                      ) : null}
                    </div>
                  ) : null}
                </>
              )}
            </>
          }
        </>
      )}
    </>
  );
};

export default TitleLayout;